<script setup lang="ts">

// 1. Imports
    import ChatMessage from './ChatMessage.ce.vue';
    import ChatHeader from './ChatHeader.ce.vue';
    import ChatInput from './ChatInput.ce.vue';
    import ChatThinkingIndicator from './ChatThinkingIndicator.ce.vue';
    import ToggleChatButton from './ToggleChatButton.ce.vue';
    import { useChatEventBus } from '@chatbot/composables/useChatEventBus';
    import { lightenColor } from '@chatbot/utils/color';
    import { type Message, type ChatOptions } from '@chatbot/typings/types';
    import { useChat } from '@chatbot/composables/useChat';

    // 2. Composables
    const { t } = useI18n();
    const chatOptions = inject<ChatOptions>('chatOptions');
    const eventBus = useChatEventBus();

    // 3. Refs / Reactive state
    const isFullscreen = ref<boolean>(false);
    const isChatOpen = ref<boolean>(true);
    const messagesContainer = ref<HTMLElement | null>(null);

    // 4. Computed properties
    const primaryColor = computed<string>(() => chatOptions?.theme?.primaryColor ?? '#00453a');
    const title = computed<string>(() => chatOptions?.title ?? 'AI Chat Assistant');
    const avatarUrl = computed<string>(() => chatOptions?.avatarUrl ?? '');
    const presenceStatus = computed<string>(() => chatOptions?.presenceStatus ?? 'Online');
    const showFullscreenButton = computed<boolean>(() => chatOptions?.showFullscreenButton ?? false);
    const maxWidth = computed<string>(() => chatOptions?.maxWidth ?? '400px');
    const mode = computed<'float' | 'fixed'>(() => chatOptions?.mode ?? 'fixed');
    const xPosition = computed<string>(() => chatOptions?.position?.xPosition ?? '20px');
    const yPosition = computed<string>(() => chatOptions?.position?.yPosition ?? '20px');
    const initialMessages = computed<Message[]>(() => chatOptions?.initialMessages ?? []);
    const n8nWebhookChatUrl = computed<string>(() => chatOptions?.n8nWebhookChatUrl ?? '');
    const venueId = computed<string>(() => chatOptions?.venueId ?? '');
    const welcomeMessage = computed<string>(() => chatOptions?.welcomeMessage ?? t('chat.welcomeMessage'));
    const showFloatingButtonTooltip = computed<boolean>(() => chatOptions?.showFloatingButtonTooltip ?? true);
    const floatingButtonTooltip = computed<string>(() => chatOptions?.floatingButtonTooltip ?? 'Chat with us');
    const tooltipBackgroundColor = computed<string>(() => lightenColor(primaryColor.value, 0.85));
    const containerStyle = computed<Record<string, string>>(() => ({
        '--chat-primary-color': primaryColor.value,
        '--chat-container-max-width': maxWidth.value,
        '--chat-x-position': xPosition.value,
        '--chat-y-position': yPosition.value,
        '--chat-tooltip-bg-color': tooltipBackgroundColor.value,
    }));

    // Initialize chat composable
    const { messages, isThinking, isLoadingPreviousConversation, sendMessage, newConversation, sendPendingMessage } = useChat({
        initialMessages: initialMessages.value,
        n8nWebhookUrl: n8nWebhookChatUrl.value || '',
        metadata: { venueId: venueId.value },
        welcomeMessage: welcomeMessage.value,
    });

    // 5. Methods / Functions
    const scrollToBottom = async(): Promise<void> => {
        await nextTick();
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    };

    const handleSendMessage = (text: string): void => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        sendMessage(text);
    };

    const handleNewConversation = (): void => {
        newConversation();
    };

    const toggleFullscreen = async(): Promise<void> => {
        isFullscreen.value = !isFullscreen.value;

        if (isFullscreen.value && document.documentElement.requestFullscreen) {
            await document.documentElement.requestFullscreen();
        } else if (!isFullscreen.value && document.exitFullscreen) {
            await document.exitFullscreen();
        }
    };

    const toggleChat = (): void => {
        isChatOpen.value = !isChatOpen.value;
    };

    // 6. Lifecycle hooks
    onMounted(() => {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        eventBus.on('scroll-to-bottom', scrollToBottom);
    });

    onUnmounted(() => {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        eventBus.off('scroll-to-bottom', scrollToBottom);
    });

    // 7. Expose
    defineExpose({
        messages,
        handleSendMessage,
        handleNewConversation,
        sendMessage: handleSendMessage,
        sendPendingMessage,
        getMessages: () => messages.value,
        clearMessages: handleNewConversation,
        newConversation: handleNewConversation,
    });
</script>

<template>
    <div
        class="chat-wrapper"
        :class="{ float: mode === 'float' }"
        :style="containerStyle"
    >
        <!-- Toggle Chat Button Component -->
        <ToggleChatButton
            :is-open="isChatOpen"
            :mode="mode"
            :avatar-url="avatarUrl"
            :title="title"
            :primary-color="primaryColor"
            :show-tooltip="showFloatingButtonTooltip"
            :tooltip-text="floatingButtonTooltip"
            :x-position="xPosition"
            :y-position="yPosition"
            @toggle="toggleChat"
        />

        <!-- Chat Container -->
        <div
            v-if="mode === 'fixed' || isChatOpen"
            class="chat-container"
            :class="{ fullscreen: isFullscreen, 'mode-float': mode === 'float' }"
        >
            <ChatHeader
                :title="title"
                :avatar="avatarUrl"
                :status="presenceStatus"
                :show-fullscreen-button="showFullscreenButton"
                :is-fullscreen="isFullscreen"
                :primary-color="primaryColor"
                @new-conversation="handleNewConversation"
                @toggle-fullscreen="toggleFullscreen"
            />

            <div
                ref="messagesContainer"
                class="messages-container"
            >
                <!-- Loading indicator for previous conversation -->
                <div
                    v-if="isLoadingPreviousConversation"
                    class="loading-container"
                >
                    <div class="loading-spinner" />
                    <p class="loading-text">
                        {{ t('chat.loadingPreviousConversation') }}
                    </p>
                </div>

                <!-- Messages -->
                <template v-else>
                    <ChatMessage
                        v-for="message in messages"
                        :key="message.id"
                        :message="message"
                        :primary-color="primaryColor"
                    />
                    <ChatThinkingIndicator v-if="isThinking" />
                </template>
            </div>

            <ChatInput
                :primary-color="primaryColor"
                :is-disabled="isThinking"
                @send="handleSendMessage"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.chat-wrapper {
    display: block;
    block-size: 100%;
    inline-size: 100%;
}

.chat-container {
    display: flex;
    flex-direction: column;
    background: var(--background-secondary-highlight);
    block-size: 100%;
    box-shadow: 0 0 20px rgb(0 0 0 / 0.1);
    font-family: var(--font-primary);
    font-size: var(--font-regular);
    inline-size: 100%;
    margin-block: 0;
    margin-inline: auto;
}

.chat-container.mode-float {
    position: fixed;
    z-index: 1000;
    overflow: hidden;
    border-radius: var(--border-radius-sm);
    animation: slide-up 0.3s ease;
    block-size: 600px;

    /* Chat window positioned above the toggle button (button is 60px + 10px gap) */
    inset-block-end: calc(var(--chat-y-position) + 70px);
    inset-inline-end: var(--chat-x-position);
    max-block-size: calc(100vh - 120px);
    max-inline-size: var(--chat-container-max-width);
    min-inline-size: var(--chat-container-max-width);
}

@keyframes slide-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.chat-container.fullscreen {
    position: fixed;
    z-index: 9999;
    margin: 0;
    block-size: 100vh;
    box-shadow: none;
    inline-size: 100vw;
    inset-block-start: 0;
    inset-inline-start: 0;
    max-inline-size: 100%;
}

/* Mobile Responsive Styles */
@media (width <= 768px) {
    .chat-container {
        inline-size: 100%;
        max-inline-size: 100%;
        min-inline-size: unset;
    }

    .chat-container.mode-float {
        inline-size: calc(100vw - var(--spacing-05));
        inset-block-end: calc(var(--chat-y-position) + 70px);
        inset-inline: var(--spacing-02);
        max-inline-size: calc(100vw - var(--spacing-05));
    }
}

@media (width <= 480px) {
    .chat-container.mode-float {
        inline-size: calc(100vw - var(--spacing-02));
        inset-inline: 5px;
        max-block-size: calc(100vh - 100px);
        max-inline-size: calc(100vw - var(--spacing-02));
    }

    .messages-container {
        padding: var(--spacing-03);
    }
}

.messages-container {
    flex: 1;
    padding: calc(var(--spacing-base) * 5);
    background: var(--background-primary);
    min-block-size: 50px;
    overflow-y: auto;
    scroll-behavior: smooth;
}

.messages-container::-webkit-scrollbar {
    inline-size: var(--size-0);
}

.messages-container::-webkit-scrollbar-track {
    background: var(--chat-scrollbar-track, rgb(241 241 241));
}

.messages-container::-webkit-scrollbar-thumb {
    border-radius: var(--border-radius-sm);
    background: var(--border-tertiary);
}

.messages-container::-webkit-scrollbar-thumb:hover {
    background: var(--border-secondary);
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-04);
    min-block-size: 200px;
}

.loading-spinner {
    border: 4px solid rgb(0 0 0 / 0.1);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    block-size: var(--spacing-06);
    border-block-start-color: var(--chat-primary-color);
    inline-size: var(--spacing-06);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    color: var(--text-secondary);
    font-size: var(--font-regular);
    margin-block-start: var(--spacing-03);
}
</style>
